/**
 * Swaran Soft Analytics Utility
 * Wraps GA4 gtag(), GTM dataLayer.push(), and PostHog for consistent CTA event tracking.
 *
 * GA4 Measurement ID : G-PQEBJ3CS74
 * GTM Container ID   : GTM-5ZQBPFZK
 *
 * Usage:
 *   import { trackEvent } from "@/lib/analytics";
 *   trackEvent("book_demo_click", { location: "homepage_hero" });
 */

import posthog from "posthog-js";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: Record<string, unknown>[];
  }
}

export type AnalyticsEventName =
  | "book_demo_click"
  | "whatsapp_cta_click"
  | "pdf_download"
  | "lead_form_submit"
  | "contact_form_submit"
  | "blog_lead_form_submit"
  | "industry_lead_form_submit"
  | "nav_cta_click"
  | "hero_cta_click"
  | "case_study_view"
  | "portfolio_filter"
  | "ai_assessment_start"
  | "book_consultation_click"
  | "join_team_click"
  | "careers_cta_click"
  | "lead_submitted"
  // Agent Mira chatbot events
  | "mira_chat_open"
  | "mira_chat_close"
  | "mira_tab_switch"
  | "mira_language_select"
  | "mira_name_submit"
  | "mira_intent_select"
  | "mira_qualify_submit"
  | "mira_email_submit"
  | "mira_mobile_submit"
  | "mira_mobile_skip"
  | "mira_handoff_cta_click"
  | "mira_notification_shown"
  | "mira_notification_click"
  | "mira_voice_connect"
  | "mira_voice_end"
  | "mira_voice_interrupt"
  | "mira_voice_text_submit";

export interface AnalyticsEventParams {
  /** Page section or component where the event fired, e.g. "homepage_hero" */
  location?: string;
  /** Label for the resource, e.g. PDF file name or blog slug */
  label?: string;
  /** Industry vertical, e.g. "manufacturing", "bfsi" */
  industry?: string;
  /** City for GEO pages, e.g. "gurugram" */
  city?: string;
  /** Blog article slug */
  blog_slug?: string;
  /** Service name */
  service?: string;
  /** Any additional string value */
  value?: string;
  /** Page identifier for multi-page tracking */
  page?: string;
  /** Section within a page, e.g. "hero", "footer", "roles" */
  section?: string;
  /** Lead ID for conversion tracking */
  lead_id?: string;
  /** Numeric value for conversion events */
  event_category?: string;
  /** Event label */
  event_label?: string;
  /** Agent Mira conversation stage, e.g. "s1_intent", "s5_handoff" */
  stage?: string;
}

/**
 * Push a custom event to both GA4 and GTM dataLayer.
 * Safe to call even before the scripts have loaded — events are queued.
 */
export function trackEvent(
  eventName: AnalyticsEventName,
  params: AnalyticsEventParams = {}
): void {
  try {
    // GA4 via gtag
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", eventName, params);
    }

    // GTM dataLayer push (GTM can pick up custom events for conversion tags)
    if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: eventName,
        ...params,
      });
    }

    // PostHog
    posthog.capture(eventName, params);
  } catch (err) {
    // Never break the UI due to analytics errors
    console.warn("[Analytics] trackEvent error:", err);
  }
}

/**
 * Track a page view manually (useful for SPA route changes).
 * GA4 auto-tracks page views on load; call this on route change if needed.
 */
export function trackPageView(path: string, title?: string): void {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("config", "G-PQEBJ3CS74", {
        page_path: path,
        page_title: title,
      });
    }
    if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: "page_view",
        page_path: path,
        page_title: title,
      });
    }

    // PostHog
    posthog.capture("$pageview", { $current_url: window.location.href });
  } catch (err) {
    console.warn("[Analytics] trackPageView error:", err);
  }
}
